import { useEffect, useState } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { useParams } from '@tanstack/react-router';
import { BlockStack, Box, Card, Link, Typography } from '@pv-frontend/ui-components-web';
import { crawlRunQuery } from '../relay/crawl-run-query';
import type { crawlRunQuery as CrawlRunQuery } from '../relay/__generated__/crawlRunQuery.graphql';
import { usePollingFetchKey } from '../relay/use-polling-fetch-key';
import { isActiveStatus } from './crawl-format';
import { CrawlRunBreakdown, type CrawlSiteRow } from './crawl-run-breakdown';
import type { CrawlRunRow } from './crawls-runs-list';

/**
 * One crawl run's per-agent drill-in (pv-frontend#163), reached from the runs
 * list. Polls (~5s) while the run is still `Queued`/`Running` so the breakdown
 * fills in live as sites complete, and goes quiet once the run is terminal.
 * Renders a not-found card when no run has the id.
 */
export function CrawlRunDetailScreen() {
    const { runId } = useParams({ from: '/listings/crawls/$runId' });
    const [active, setActive] = useState(false);
    const pollKey = usePollingFetchKey(active);

    const data = useLazyLoadQuery<CrawlRunQuery>(
        crawlRunQuery,
        { id: runId },
        { fetchKey: pollKey, fetchPolicy: 'store-and-network' },
    );
    const detail = data.crawlRun;
    const status = detail?.run.status;

    useEffect(() => {
        setActive(status ? isActiveStatus(status) : false);
    }, [status]);

    return (
        <Box padding="600" maxWidth={1080}>
            <BlockStack gap="500">
                <Link href="/listings/crawls">← Back to crawls</Link>
                {detail ? (
                    <CrawlRunBreakdown run={mapRun(detail.run)} sites={detail.sites.map(mapSite)} />
                ) : (
                    <Card>
                        <Typography tone="text-secondary">
                            No crawl run was found for this id. It may have been pruned.
                        </Typography>
                    </Card>
                )}
            </BlockStack>
        </Box>
    );
}

type RunData = CrawlRunQuery['response']['crawlRun'];
type RunSummary = NonNullable<RunData>['run'];
type SiteData = NonNullable<RunData>['sites'][number];

function mapRun(run: RunSummary): CrawlRunRow {
    return {
        id: run.id,
        trigger: run.trigger,
        requestedByOperator: run.requestedByOperator,
        scope: run.scope,
        status: run.status,
        requestedUtc: run.requestedUtc,
        startedUtc: run.startedUtc ?? null,
        completedUtc: run.completedUtc ?? null,
        siteId: run.siteId ?? null,
        sitesCrawled: run.sitesCrawled,
        sitesPartiallyCrawled: run.sitesPartiallyCrawled,
        listingsAdded: run.listingsAdded,
        listingsUpdated: run.listingsUpdated,
        listingsUnchanged: run.listingsUnchanged,
        listingsMissed: run.listingsMissed,
        listingsDelisted: run.listingsDelisted,
        rejectsQueued: run.rejectsQueued,
    };
}

function mapSite(site: SiteData): CrawlSiteRow {
    return {
        siteId: site.siteId,
        siteName: site.siteName,
        disposition: site.disposition,
        coverage: site.coverage,
        coverageNote: site.coverageNote ?? null,
        listingsAdded: site.listingsAdded,
        listingsUpdated: site.listingsUpdated,
        listingsUnchanged: site.listingsUnchanged,
        urlsInSitemap: site.urlsInSitemap,
        rejectsQueued: site.rejectsQueued,
        listingsMissed: site.listingsMissed,
        listingsDelisted: site.listingsDelisted,
        error: site.error ?? null,
        startedUtc: site.startedUtc,
        completedUtc: site.completedUtc,
    };
}
